import { DataSchemaTypes } from "../../utils/Constants";
import { DataSchemaType, InputRegistry, PhaseManifest } from "../../utils/Interfaces";

/**
 * Retrieves the data bucket for a specific phase from the state arrays.
 * 
 * @param {string} slug - The unique phase identifier (e.g., 'pm_identity_access').
 * @param {input | faults} bucketType - The specific state array to search within.
 * 
 * @returns {Object} - Returns the key-value pairs for the requested phase, or an empty object if not found.
 */
export const getPhaseBucket = (slug: string, bucketType: any): object => {
    // Find the object in the array that contains the slug as a key
    const entry = bucketType.find((phaseObj: any) => Object.prototype.hasOwnProperty.call(phaseObj, slug));

    // Return the nested object for that slug, or a fallback to prevent render crashes
    return entry ? entry[slug] : {};
};

/**
 * Lookup the value of a specific element in the state arrays based on the `slug` and `input id`.
 * 
 * @param {string} xid - The name/id of the element whose value you want to retrieve.
 * @param {string} slug - The unique phase identifier (e.g., 'pm_identity_access').
 * @param {'input' | 'faults'} bucketType - The specific state array to search within.
 * 
 * @returns {any | undefined} - Returns the value of the field if it exists, or `undefined` if the element is not found.
 */
export const lookupElement = (xid: string, slug: string, bucketType: any): any | undefined => {
    const entry = bucketType.find((phaseObj: any) => Object.prototype.hasOwnProperty.call(phaseObj, slug));

    // Return the value for that slug, or an empty fallback to prevent render crashes
    return entry ? entry[slug][xid] : undefined;
};

/**
 * Modifies a specific field value within a phase-keyed object inside a state array.
 * Works for any bucket (input, errors, etc.) and handles both simple values and array toggles (checkboxes).
 *
 * @param {string} xid - The specific element ID/Key to update.
 * @param {string} slug - The unique phase identifier (e.g., 'pm_identity_access').
 * @param {any} finalValue - The value to set (or toggle).
 * @param {'input' | 'faults'} bucketType - The specific state array to search within.
 * @param {boolean} isCheckbox - Special route for checkboxes
 * 
 * @returns {Array} - A new array with the immutable updates applied.
 */
export const modifyElement = (xid: string, slug: string, finalValue: any, bucketType: any[], isCheckbox: boolean = false): any[] => {
    return bucketType.map((section) => {
        // Check if this is the phase/section we want to update
        if (!Object.prototype.hasOwnProperty.call(section, slug)) {
            return section; // Return as is if not the target
        }

        // 2. Access the current data for this phase
        const currentPhaseData = section[slug];

        // 3. Special Logic for Checkboxes (Toggle inside array)
        if (isCheckbox) {
            const existingValues = Array.isArray(currentPhaseData[xid]) ? currentPhaseData[xid] : [];
            finalValue = existingValues.includes(finalValue)
                ? existingValues.filter((v: any) => v !== finalValue) // Remove
                : [...existingValues, finalValue];                   // Add
        }

        // 4. Return the new object structure for this index
        return {
            ...section,
            [slug]: {
                ...currentPhaseData,
                [xid]: finalValue
            }
        };
    });
};

/**
 * Reconciles a phase state by applying default values and purging data
 * that is no longer required due to visibility or requirement rules.
 *
 * @param {string} slug - The unique phase identifier (e.g., 'pm_identity_access').
 * @param {any} finalPhaseData - The new object or updated data to set for this phase.
 * @param {'input' | 'faults'} bucketType - The specific state array to search within.
 * 
 * @returns {Array} - A new array with the immutable updates applied.
 */
export const reconcilePhaseState = (slug: string, finalPhaseData: any, bucketType: any[]): any[] => {
    return bucketType.map((section) => {
        // If this section doesn't contain our target phase, skip it
        if (!Object.prototype.hasOwnProperty.call(section, slug)) {
            return section;
        }

        // Complete replacement: The old state for this slug is discarded entirely.
        return {
            ...section,
            [slug]: finalPhaseData
        };
    });
};

/**
 * Pinpoints a specific field definition using both the parent Attribute ID and the specific Field ID.
 * 
 * @param {string} elementId - The 'g' ID of the nested source element.
 * @param {string} attrId - The 'g' ID of the top-level attribute object.
 * @param {Array} attributes - The specific state array to search within (e.g. state.data.attributes)
 * 
 * @returns {Object | null} - The specific field definition or null.
 */
export const findAttributeDefinition = (elementId: string, attrId: string, attributes: any[]): any | null => {
    // Find the parent attribute block first
    const parent = attributes.find(attr => attr.g === attrId);
    if (!parent) return null;

    // Search only within that parent's 's' (sources) array
    return parent.s.find((field: any) => field.g === elementId) || null;
};

/**
 * Resolves a specific input definition from the registry by its unique name.
 * 
 * @param {InputRegistry[]} registry - The manifest of all available inputs.
 * @param {string} targetName - The unique identifier (slug) to locate.
 * 
 * @returns {InputRegistry | undefined} The resolved input definition.
 */
export const inputRegistryLookupOld = (registry: InputRegistry[], targetName: string): InputRegistry | undefined => {
    const input = registry.find((item) => item.name === targetName);

    if (!input) {
        // Precise MNC-style logging
        console.error(`[Registry Error]: Definition for '${targetName}' not found in manifest.`);
        return undefined;
    }

    return input;
};

/**
 * Generates an initial system value for a profile attribute based on its 
 * structural definition and validation constraints.
 * 
 * ## Architectural Logic:
 * - **Alphanumeric**: Defaults to 0 for numeric-centric validations to ensure 
 *   mathematical compatibility, otherwise empty string.
 * - **Selections**: Multi-select structures (checkbox/listbox) utilize arrays 
 *   to facilitate collection-based mapping.
 * - **Binary Control**: Toggles strictly default to the defined 'false' state 
 *   to ensure 'Safe-by-Default' configurations (e.g., Read-only).
 * - **Object States**: Dates and Single-selects utilize null to signify 
 *   a 'Neutral' or 'Unselected' state, distinct from empty data.
 * 
 * @param {InputRegistry} definition - The structural blueprint containing display_type and validation rules.
 * 
 * @returns {string | number | boolean | Array | null} The resolved primitive or collection for the initial state.
 */
export const generateDefaultValue = (definition: any): string | number | boolean | Array<any> | null => {
    let defaultValue: any;
    const {
        type: display_type,
        val: validation,
        toggles,
        allow_multiple
    } = definition;

    switch (display_type) {
        // --- Alphanumeric Entry ---
        case 'text':
        case 'numeric':
        case 'richtext':
        case 'telephone':
            // Logic: Numeric-focused validations default to 0, otherwise empty string
            if (['amounts', 'number_formats'].includes(validation)) {
                defaultValue = 0;
            } else {
                defaultValue = '';
            }
            break;

        // --- Selection Logic ---
        case 'predefined':
        case 'dropdown':
        case 'radio':
        case 'checkbox':
            // Logic: Multi-select lists (checkboxes or marked multiple) use arrays
            const isMulti = definition.allow_multiple || display_type === 'checkbox';
            defaultValue = isMulti ? [] : null;
            break;

        // --- Binary State Management ---
        case 'toggle':
            /** 
             * Toggles defaults to 'false' state value.
             * e.g., 'read_only' or 'optional' 
             */
            defaultValue = toggles?.false?.key ?? null;
            break;

        // --- Specialized Objects ---
        case 'date':
            // Logic: Null represents an unselected calendar state
            defaultValue = null;
            break;

        case 'file':
            // Logic: An array facilitates the "File List" pattern for multiple uploads
            defaultValue = [];
            break;

        default:
            defaultValue = '';
            break;
    }

    return defaultValue;
};

/**
 * Resolves a specific input definition from a multi-phase manifest by its unique name.
 * 
 * This resolver performs a deep search across all architectural phases (Identity, 
 * Configuration, etc.) to locate the specified attribute definition.
 * 
 * @param {PhaseManifest[]} manifest - The multi-phase manifest containing nested definitions.
 * @param {string} targetName - The unique identifier (name) of the attribute to locate.
 * 
 * @returns {InputRegistry | undefined} The resolved attribute definition or undefined if not found.
 */
export const inputRegistryLookup = (manifest: any[], targetName: string): any | undefined => {
    // 1. Iterate through phases and search their nested 'definitions' (or 'block')
    let resolvedDefinition;

    for (const phase of manifest) {
        // Search the current phase's definitions array
        const found = phase.definitions?.find((field: any) => field.name === targetName);

        if (found) {
            resolvedDefinition = found;
            break; // Exit loop immediately once the identity is established
        }
    }

    if (!resolvedDefinition) {
        // Precise MNC-style logging for system reconciliation
        console.error(`[Registry Error]: Definition for '${targetName}' not found in any phase of the manifest.`);
        return undefined;
    }

    return resolvedDefinition;
};

/**
 * Retrieves the nested definitions collection for a specific architectural phase.
 * 
 * @param {InputRegistry[]} registry - The input registry that contains the phases.
 * @param {string} phaseSlug - The unique identifier (e.g., 'identity_and_access').
 * 
 * @returns {InputRegistry[] | undefined} The collection of field definitions or an empty array.
 */
export const getPhaseDefinitions = (registry: any[], phaseSlug: string): any[] | undefined => {
    const phase = registry.find(p => p.slug === phaseSlug);

    if (!phase) {
        console.error(`[Registry Error]: Phase '${phaseSlug}' not found.`);
        return [];
    }

    return phase.definitions || undefined;
};

/**
 * Resolves complex relational logic based on a provided ruleset and current state.
 * 
 * This engine evaluates "Logic Gates" (AND/OR) against a set of rules to determine 
 * system behaviors such as conditional visibility or dynamic mandates. It supports 
 * polymorphic data types including primitives, InputOption objects, and collections.
 * 
 * @param {object} blueprint - The specific logic block to evaluate (e.g., definition.visibility).
 * @param {object} values - The current state bucket used for data reconciliation.
 * 
 * @returns {boolean} True if the current state satisfies the defined constraints.
 */
export const resolveManifestLogic = (blueprint: any, values: any): boolean => {
    // 1. Guard: If the blueprint is missing, the logic passes by default
    if (!blueprint) return true;

    const { logic = 'and', rules = [] } = blueprint;

    // 2. Logic Gate: 'and' (.every) vs 'or' (.some)
    const evaluationMethod = logic === 'or' ? 'some' : 'every';

    return rules[evaluationMethod]((rule: any) => {
        // --- Data Resolution ---
        const dependentValue = values[rule.depends_on];
        let activeKey = dependentValue;

        if (dependentValue && typeof dependentValue === "object") {
            activeKey = Array.isArray(dependentValue)
                ? dependentValue.map(v => (typeof v === "object" ? v?.key : v))
                : dependentValue?.key;
        }

        const allowedKeys = Array.isArray(rule.key) ? rule.key : [rule.key];

        // --- Operator Execution ---
        switch (rule.operator) {
            case 'is':
            case 'any':
                if (Array.isArray(activeKey)) {
                    return activeKey.some(item => allowedKeys.includes(item));
                }
                return allowedKeys.includes(activeKey);

            case 'has_schema_options':
                const schema = DataSchemaTypes.find(t => t.key === activeKey);
                return (schema?.validations?.length ?? 0) > 0;

            case 'not':
                return !allowedKeys.includes(activeKey);

            case 'exists':
                return Array.isArray(activeKey) ? activeKey.length > 0 : !!activeKey;

            default:
                return true;
        }
    });
};

/**
 * Resolves a full Schema Definition object by its unique key.
 * @param key - The schema name (e.g., 'text', 'numeric')
 * 
 * @returns The DataSchemaType object or undefined
 */
export const resolveDataSchemaByKey = (key: string | undefined): DataSchemaType | undefined => {
    if (!key) return undefined;

    return DataSchemaTypes.find((schema: any) => schema.key === key);
};

export const initializePhasing = (phases: PhaseManifest[], activeSlug: string = 'pm_identity_access') => {
    // Ensure the manifest is sorted by sequence
    const sortedManifest = [...phases].sort((a, b) => a.sequence - b.sequence);

    // Locate the current phase index
    const currentIndex = sortedManifest.findIndex(p => p.slug === activeSlug);
    const current = sortedManifest[currentIndex] || sortedManifest[0];

    // Resolve Previous/Next references
    const prevPhase = sortedManifest[currentIndex - 1];
    const nextPhase = sortedManifest[currentIndex + 1];

    return {
        manifest: sortedManifest,
        current,
        previous: prevPhase ? { sequence: prevPhase.sequence, slug: prevPhase.slug } : null,
        next: nextPhase ? { sequence: nextPhase.sequence, slug: nextPhase.slug } : null,
    };
};
